import React, { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Brush,
} from "recharts";
import { format, parseISO } from "date-fns";
import { ChartConfig, ChartDataTransformed } from "@/types/price-index";

interface PriceChartProps {
  data: ChartDataTransformed[];
  config?: Partial<ChartConfig>;
  showArea?: boolean;
  enableZoom?: boolean;
  height?: number;
}

const defaultConfig: ChartConfig = {
  height: 400,
  margin: { top: 10, right: 30, left: 0, bottom: 0 },
  lineColor: "#2D96A7",
  areaColor: "#2D96A71A",
  gridColor: "#E1E6F1",
  tooltipEnabled: true,
  legendEnabled: true,
  animationDuration: 1000,
  xAxisKey: "date",
  yAxisKey: "price",
  showDataPoints: true,
  smoothCurve: true,
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-grey-3 rounded-lg p-3 shadow-lg">
        <p className="text-sm font-semibold text-main-900 mb-2">
          {format(parseISO(label), "MMM dd, yyyy")}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm text-grey-4">
            <span className="font-medium">{entry.name}:</span>{" "}
            <span className="font-semibold text-bnp-cyan">
              ₦{entry.value.toLocaleString()}
            </span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const PriceChart: React.FC<PriceChartProps> = ({
  data,
  config,
  showArea = false,
  enableZoom = true,
  height = 400,
}) => {
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);

  const chartConfig = useMemo(() => {
    return { ...defaultConfig, ...config, height };
  }, [config, height]);

  const formattedData = useMemo(() => {
    return data.map((item) => ({
      ...item,
      formattedDate: format(parseISO(item.date), "MMM dd"),
    }));
  }, [data]);

  const ChartComponent = showArea ? AreaChart : LineChart;

  const handleBrushChange = (brushIndex: any) => {
    if (
      brushIndex &&
      brushIndex.startIndex !== undefined &&
      brushIndex.endIndex !== undefined
    ) {
      setZoomDomain([brushIndex.startIndex, brushIndex.endIndex]);
    }
  };

  const dataKeys = useMemo(() => {
    if (formattedData.length === 0) return [];
    const keys = Object.keys(formattedData[0]).filter(
      (key) =>
        key !== "date" &&
        key !== "formattedDate" &&
        typeof (formattedData[0] as Record<string, any>)[key] === "number",
    );
    return keys;
  }, [formattedData]);

  if (!formattedData || formattedData.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-weak-100 rounded-xl border border-grey-3"
        style={{ height: chartConfig.height }}
      >
        <p className="text-grey-4 text-sm">No data available</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <ResponsiveContainer width="100%" height={chartConfig.height}>
        <ChartComponent data={formattedData} margin={chartConfig.margin}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={chartConfig.gridColor}
            vertical={false}
          />
          <XAxis
            dataKey="formattedDate"
            tick={{ fontSize: 12, fill: "#868C98" }}
            tickLine={false}
            axisLine={{ stroke: "#E1E6F1" }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: "#868C98" }}
            tickLine={false}
            axisLine={{ stroke: "#E1E6F1" }}
            tickFormatter={(value) => `₦${(value / 1000000).toFixed(1)}M`}
          />
          {chartConfig.tooltipEnabled && (
            <Tooltip content={<CustomTooltip />} />
          )}
          {chartConfig.legendEnabled && (
            <Legend
              wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }}
              iconType="line"
            />
          )}
          {enableZoom && (
            <Brush
              dataKey="formattedDate"
              height={30}
              stroke={chartConfig.lineColor}
              fill="#F6F8FA"
              onChange={handleBrushChange}
            />
          )}
          {showArea
            ? dataKeys.map((key, index) => (
                <Area
                  key={key}
                  type={chartConfig.smoothCurve ? "monotone" : "linear"}
                  dataKey={key}
                  stroke={
                    index === 0
                      ? chartConfig.lineColor
                      : `hsl(${index * 60}, 70%, 50%)`
                  }
                  fill={
                    index === 0
                      ? chartConfig.areaColor
                      : `hsla(${index * 60}, 70%, 50%, 0.1)`
                  }
                  strokeWidth={2}
                  dot={chartConfig.showDataPoints ? { r: 3 } : false}
                  activeDot={{ r: 5 }}
                  animationDuration={chartConfig.animationDuration}
                  name={
                    key.charAt(0).toUpperCase() +
                    key.slice(1).replace(/([A-Z])/g, " $1")
                  }
                />
              ))
            : dataKeys.map((key, index) => (
                <Line
                  key={key}
                  type={chartConfig.smoothCurve ? "monotone" : "linear"}
                  dataKey={key}
                  stroke={
                    index === 0
                      ? chartConfig.lineColor
                      : `hsl(${index * 60}, 70%, 50%)`
                  }
                  strokeWidth={2}
                  dot={chartConfig.showDataPoints ? { r: 3 } : false}
                  activeDot={{ r: 5 }}
                  animationDuration={chartConfig.animationDuration}
                  name={
                    key.charAt(0).toUpperCase() +
                    key.slice(1).replace(/([A-Z])/g, " $1")
                  }
                />
              ))}
        </ChartComponent>
      </ResponsiveContainer>
    </div>
  );
};

export default PriceChart;
